// Destinos del portal según los permisos «home» del usuario. El orden de PORTALS es el orden en
// que se muestran en el selector y define el destino por defecto.
import type { Scope } from '@/api/types/permissions';
import type { AccessState } from '@/lib/authGuard';

export type PortalId = 'retail-admin' | 'credit' | 'sales' | 'admin';

export type PortalPath = '/retail-admin' | '/credit' | '/sales' | '/tenant/users';

export interface Portal {
    id: PortalId;
    homePermission: string;
    path: PortalPath;
    /** Alcance al que opera el portal: elige qué asignación identifica al usuario en su shell. */
    operatingScope: Scope;
}

export const PORTALS: readonly Portal[] = [
    {
        id: 'retail-admin',
        homePermission: 'retail.admin.home.read',
        path: '/retail-admin',
        operatingScope: 'LEVEL_1',
    },
    {
        id: 'credit',
        homePermission: 'loan.admin.home.read',
        path: '/credit',
        operatingScope: 'TENANT',
    },
    {
        id: 'sales',
        homePermission: 'retail.seller.home.read',
        path: '/sales',
        operatingScope: 'LEVEL_3',
    },
    {
        id: 'admin',
        homePermission: 'tenant.user.read',
        path: '/tenant/users',
        operatingScope: 'TENANT',
    },
];

/** Portales a los que el usuario puede entrar, en el orden fijo de PORTALS. */
export function portalsFor(permissions: ReadonlySet<string>): Portal[] {
    return PORTALS.filter((portal) => permissions.has(portal.homePermission));
}

/** Portal al que pertenece la ruta, o `null` si está fuera de los portales del selector. */
export function portalForPathname(pathname: string): Portal | null {
    return (
        PORTALS.find(
            (portal) => pathname === portal.path || pathname.startsWith(`${portal.path}/`),
        ) ?? null
    );
}

/** Ruta del primer portal del usuario, o `null` si no tiene ninguno. */
export function defaultPortalPath(permissions: ReadonlySet<string>): PortalPath | null {
    return portalsFor(permissions)[0]?.path ?? null;
}

/** Con 2 o más portales el usuario puede cambiar entre ellos. */
export function hasMultiplePortals(permissions: ReadonlySet<string>): boolean {
    return portalsFor(permissions).length >= 2;
}

export const NO_ACCESS_PATH = '/no-access';
export const SELECT_ROLE_PATH = '/select-role';

export type AccessTarget = PortalPath | typeof NO_ACCESS_PATH | typeof SELECT_ROLE_PATH;

/** Destino de `/`: 0 portales → /no-access, 1 → ese portal, varios → selector. */
export function entryTarget(access: AccessState): AccessTarget | null {
    if (access.kind !== 'ready') return null;
    const portals = portalsFor(access.permissions);
    if (portals.length === 0) return NO_ACCESS_PATH;
    if (portals.length === 1) return portals[0].path;
    return SELECT_ROLE_PATH;
}

/** Igual que `entryTarget`, pero con varios portales el selector se queda. */
export function selectorTarget(access: AccessState): AccessTarget | null {
    const target = entryTarget(access);
    return target === SELECT_ROLE_PATH ? null : target;
}

/** Sin el permiso home del portal, a dónde mandar al usuario; `null` si puede entrar. */
export function portalGuardTarget(portalId: PortalId, access: AccessState): AccessTarget | null {
    if (access.kind !== 'ready') return null;
    const portal = PORTALS.find((p) => p.id === portalId);
    if (portal && access.permissions.has(portal.homePermission)) return null;
    return defaultPortalPath(access.permissions) ?? NO_ACCESS_PATH;
}

/** En /no-access: quien sí tiene portales sale hacia ellos; `null` si debe quedarse. */
export function noAccessTarget(access: AccessState): AccessTarget | null {
    const target = entryTarget(access);
    return target === NO_ACCESS_PATH ? null : target;
}
